/**
 * Changement d'écran dans une page ouverte (liste → fiche, fiche → liste, menu, recherche, fil d'Ariane, retour),
 * animé par les transitions de page du navigateur (View Transitions, sans bibliothèque). Appelé par l'espace de travail
 * (espace.tsx) pour toute navigation qui change d'écran dans une page visible : les écrans n'ont rien à faire.
 *
 * Description du mouvement (5 informations, web/README.md › Mouvement) :
 *   - ce qui bouge : le contenu de la page (son volet) ; en-tête, barre latérale et dock restent immobiles ;
 *   - déclencheur : un changement d'écran dans la page (pas un filtre, pas une nouvelle page, pas une redirection) ;
 *   - mouvement : l'ancien écran s'efface, le nouveau apparaît en fondu avec un léger glissement vers le haut ;
 *   - durée : courte et accélérée pour la sortie, moyenne et décélérée pour l'entrée (jetons) ;
 *   - mouvement réduit : aucune transition, l'écran change directement.
 * Navigateur sans View Transitions : l'écran change directement (même résultat, sans animation).
 * Styles des pseudo-éléments ::view-transition-* : core/mouvement.css.
 */
import { flushSync } from 'react-dom';
import { mouvementReduit } from '../../design';

interface TransitionDeVue { finished: Promise<void> }
type DocumentAvecTransitions = Document & { startViewTransition?: (miseAJour: () => void) => TransitionDeVue };

/** Attribut posé sur <html> pendant la transition : les règles CSS ne visent que nos transitions. */
export const ATTRIBUT_TRANSITION = 'data-agx-transition';
/** Nom de la page qui change d'écran (une seule à la fois, même en côte à côte). */
export const NOM_PAGE = 'agx-page';
/**
 * Habillage posé au-dessus des pages (en-tête flottant, dock et ses panneaux, messages, voile, bandeau, menus
 * positionnés) : capturé à part, il reste au-dessus du contenu pendant la transition au lieu de passer dessous.
 */
const DESSUS = '.agx-entete, .ag-dock-wrap, .agx-toasts, .agx-voile, .agx-maquette, [data-radix-popper-content-wrapper]';

let generation = 0;
let nommes: HTMLElement[] = [];

function nommer(volet: HTMLElement) {
  if (!nommes.includes(volet)) {
    volet.style.setProperty('view-transition-name', NOM_PAGE);
    nommes.push(volet);
  }
  document.querySelectorAll<HTMLElement>(DESSUS).forEach((el) => {
    if (nommes.includes(el) || volet.contains(el)) return;
    el.style.setProperty('view-transition-name', `agx-dessus-${nommes.length}`);
    nommes.push(el);
  });
}

function nettoyer() {
  nommes.forEach((el) => el.style.removeProperty('view-transition-name'));
  nommes = [];
  document.documentElement.removeAttribute(ATTRIBUT_TRANSITION);
}

/**
 * Applique `miseAJour` (changement d'écran de la page `idPage`) avec une transition de vue si possible ; sinon (pas de
 * prise en charge, mouvement réduit, page masquée ou onglet en arrière-plan) l'applique directement.
 */
export function transitionEcran(idPage: string, miseAJour: () => void): void {
  const doc = document as DocumentAvecTransitions;
  if (typeof doc.startViewTransition !== 'function' || mouvementReduit() || document.visibilityState !== 'visible') {
    miseAJour();
    return;
  }
  const volet = document.querySelector<HTMLElement>(`[data-page-id="${CSS.escape(idPage)}"]`);
  if (!volet || volet.getAttribute('aria-hidden') === 'true') {
    miseAJour();
    return;
  }
  const n = ++generation;
  nommer(volet);
  document.documentElement.setAttribute(ATTRIBUT_TRANSITION, 'ecran');
  let transition: TransitionDeVue;
  try {
    // Le navigateur capture l'écran actuel, puis appelle la mise à jour : React l'applique tout de suite (flushSync)
    // pour que la nouvelle capture montre le nouvel écran ; l'habillage apparu entre-temps est nommé à son tour.
    transition = doc.startViewTransition(() => {
      flushSync(miseAJour);
      nommer(volet);
    });
  } catch {
    nettoyer();
    miseAJour();
    return;
  }
  // Une transition interrompue par la suivante ne nettoie pas les noms de celle-ci.
  const fin = () => { if (n === generation) nettoyer(); };
  transition.finished.then(fin, fin);
}
